import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';

import GradientRovoIcon from '../../common/assets/GradientRovoIcon';

const i18n = defineMessages({
	getCreativeLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.get-creative-label',
		defaultMessage: 'Get creative with Rovo',
		description: 'Label text for the Rovo header generation entry point',
	},
	getCreativePlaceholder: {
		id: 'ai-image-generation.rovo-header-image-generator.get-creative-placeholder',
		defaultMessage: 'Describe an image to create',
		description: 'Placeholder text for the Rovo header generation entry point',
	},
	rovoIconAlt: {
		id: 'ai-image-generation.rovo-header-image-generator.rovo-icon-alt',
		defaultMessage: 'Rovo icon',
		description: 'Alt text for the Rovo icon',
	},
});

const styles = cssMap({
	divider: {
		width: '100%',
		height: '1px',
		marginBlock: token('space.100'),
		backgroundColor: token('color.background.accent.gray.subtler'),
	},
	rovoIcon: {
		width: '16px',
		height: '16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	imagePromptContainer: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		justifyContent: 'center',
		borderStyle: 'solid',
		borderRadius: '8px',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		backgroundColor: token('elevation.surface'),
		paddingInline: token('space.150'),
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
	},
	stack: {
		width: '100%',
		flexDirection: 'column',
	},
});

export const RovoHeaderImageGenerator = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<>
			<Box xcss={styles.divider} />

			<Inline space="space.075" alignBlock="center">
				<Box xcss={styles.rovoIcon}>
					<GradientRovoIcon label={formatMessage(i18n.rovoIconAlt)} />
				</Box>
				<Text size="medium">{formatMessage(i18n.getCreativeLabel)}</Text>
			</Inline>

			<Box xcss={styles.imagePromptContainer}>
				{/* Not using a Stack component directly as it prevents the TextArea from expanding */}
				<Box xcss={styles.stack}>
					<TextArea placeholder={formatMessage(i18n.getCreativePlaceholder)} appearance="none" />
					<Inline space="space.075" alignInline="end">
						<IconButton appearance="primary" icon={ArrowUpIcon} label="Submit" shape="circle" />
					</Inline>
				</Box>
			</Box>
		</>
	);
};
